import React from 'react';
import {Box, Chip, Link, Stack, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {CARD_SX, Eyebrow} from './primitives.js';
import PointMap from './PointMap.js';
import ConsigneSnapshotRow from '../../layout/ConsigneSnapshotRow.js';
import {ConteneurIcon} from '../../../../utils/ui/Icons/ConteneurIcon.js';
import {helper} from '../../../../utils/helper';
import Constants from '../../../../utils/constants';
import {formatCoords} from './interventionRules.js';

const Section = ({title, children}) => (
    <Box sx={{...CARD_SX, p: 2.5}}>
        <Eyebrow sx={{mb: 1.5}}>{title}</Eyebrow>
        {children}
    </Box>
);

const Row = ({label, value, mono = false}) => (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{py: 0.5}}>
        <Typography variant="body2" sx={{color: '#8A929C'}}>{label}</Typography>
        <Typography variant="body2" sx={{fontWeight: 600, textAlign: 'right', color: mono ? '#0F2D69' : '#2D333B',
            fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined}}>
            {value ?? '—'}
        </Typography>
    </Stack>
);

/**
 * Rail de contexte (droite, 340 px) : carte + point, consignes actives ce jour-là, détails.
 * `isClient` masque la ligne « Intervention » (le DTO a déjà `releve = null` pour un client).
 */
function ContextRail({detail, isClient}) {
    const {point, releve, consignesActives = []} = detail;

    return (
        <Stack spacing={2}>
            <Box sx={{...CARD_SX}}>
                <Box sx={{p: 1.5, pb: 0}}>
                    <PointMap point={point} releve={releve}/>
                </Box>
                <Box sx={{p: 2.5}}>
                    <Typography sx={{fontSize: 20, fontWeight: 800, color: '#0F2D69', lineHeight: 1.2}}>{point.nomenclature ?? '—'}</Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1}}>
                        {point.dechetType && <Chip size="small" label={point.dechetType} sx={helper.getColorForDechetType(point.dechetType)}/>}
                        {point.conteneurType && <Chip size="small" label={point.conteneurType}/>}
                        {point.quantite != null && <Chip size="small" icon={<ConteneurIcon/>} label={`${point.quantite}`}/>}
                    </Box>
                    <Typography variant="body2" sx={{mt: 1.5, color: '#5A6470'}}>
                        {[point.adresse, point.commune].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                    <Box sx={{mt: 1.5}}>
                        <Row label="Point" value={formatCoords(point.latitude, point.longitude)}/>
                        {!isClient && releve && (
                            <Row label="Intervention" value={formatCoords(releve.latitude, releve.longitude, releve.accuracyM)}/>
                        )}
                    </Box>
                    <Typography variant="caption" sx={{display: 'block', mt: 1, color: '#8A929C'}}>
                        {[point.client, point.patrimoine].filter(Boolean).join(' · ')}
                    </Typography>
                    {point.patrimoineId != null && (
                        <Link component={RouterLink} to={Constants.APP_ROUTE_PATRIMOINE_DETAIL.replace(':id', point.patrimoineId)}
                              variant="body2" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1, fontWeight: 600}}>
                            Ouvrir la carte <OpenInNewIcon sx={{fontSize: 14}}/>
                        </Link>
                    )}
                </Box>
            </Box>

            <Section title="Consignes actives ce jour">
                {consignesActives.length > 0 ? (
                    <Stack spacing={1}>
                        {consignesActives.map((c, i) => <ConsigneSnapshotRow key={c.id ?? i} consigne={c} dense/>)}
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{color: '#8A929C', fontStyle: 'italic'}}>Aucune consigne active</Typography>
                )}
            </Section>

            <Section title="Détails">
                <Row label="Prestation" value={detail.passage?.prestation}/>
                <Row label="Identifiant" value={`#${detail.id}`} mono/>
                {detail.details?.observation?.trim() && (
                    <Box sx={{pt: 1}}>
                        <Typography variant="body2" sx={{color: '#8A929C'}}>Commentaire</Typography>
                        <Typography variant="body2" sx={{mt: 0.5, color: '#2D333B', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
                            {detail.details.observation}
                        </Typography>
                    </Box>
                )}
            </Section>
        </Stack>
    );
}

export default ContextRail;
